import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { DanelFileInfo } from '../../components/DanelFileUploadItem/types';
import { WizardStep } from '../../pages/DataFeedWizard/wizardStepsConfig';
import {
  ConfigureMappingData,
  LinkAccountsData,
  AccountCustodianMapping,
  MappingItem,
  CustodianOption,
  StepKey,
  PristineState,
  ItemMappingUpdate,
} from './types';
import { ButtonConfig } from '../../pages/DataFeedWizard/stepValidation';
import {
  BankType,
  SessionDataResponse,
  UploadedFileInfo,
} from '../../types/generatedTypes';
import { RowType } from '../../types/rowType';

interface StepActions {
  onNext?: () => Promise<boolean | number> | boolean | number;
  validateFields?: () => boolean;
}

export interface SessionNameValidation {
  isChecking: boolean;
  isUnique: boolean | null;
  error: string | null;
}

interface DataFeedWizardState {
  activeStep: WizardStep;
  activeUploadTab: 'vaults' | 'excel';
  vaultFiles: DanelFileInfo[];
  excelFiles: DanelFileInfo[];
  restoredFiles: UploadedFileInfo[]; // Files restored from session (no actual File objects)
  returnExcel: boolean;
  rowsType: RowType;
  currentStepButtonState: ButtonConfig;
  configureMappingData: ConfigureMappingData;
  linkAccountsData: LinkAccountsData;
  custodianOptions: CustodianOption[]; // Cached custodian options for step 3
  instrumentMappings: MappingItem[]; // Instrument mappings for step 4
  otcMappings: MappingItem[]; // OTC mappings for step 5
  isMoving: boolean; // Track if move operation is in progress
  stepActions: StepActions;
  sessionId: string | null;
  fileId: string | null;
  completedSteps: number[];
  stepLoading: boolean;
  sessionName: string;
  sessionNameValidation: SessionNameValidation;
  enabledRowsCount: number;
  uploadedRowsCount: number;

  // Selectors
  getInstrumentItems: () => MappingItem[];
  getOTCItems: () => MappingItem[];

  // Actions
  setActiveStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  setCurrentStepButtonState: (state: ButtonConfig) => void;
  registerStepActions: (actions: StepActions) => void;
  clearStepActions: () => void;
  setActiveUploadTab: (tab: 'vaults' | 'excel') => void;
  setVaultFiles: (files: DanelFileInfo[]) => void;
  setExcelFiles: (files: DanelFileInfo[]) => void;
  addVaultFiles: (files: DanelFileInfo[]) => void;
  addExcelFiles: (files: DanelFileInfo[]) => void;
  removeVaultFile: (index: number) => void;
  removeExcelFile: (index: number) => void;
  updateVaultFileBank: (index: number, bank: BankType) => void;
  setReturnExcel: (returnExcel: boolean) => void;
  setRowsType: (rowsType: RowType) => void;
  setConfigureMappingData: (data: Partial<ConfigureMappingData>) => void;
  addOTCColumn: () => void;
  removeOTCColumn: (index: number) => void;
  updateOTCColumn: (index: number, value: string) => void;
  updateOTCColumnIdentifier: (index: number, isIdentifier: boolean) => void;
  setLinkAccountsData: (data: Partial<LinkAccountsData>) => void;
  updateAccountMapping: (
    index: number,
    custodianId: string | null,
    custodianName: string | null,
    portfolioId: string | null,
    clientName: string | null,
    portfolioName: string | null
  ) => void;
  updateAccountUploadEnabled: (index: number, enabled: boolean) => void;
  setCustodianOptions: (options: CustodianOption[]) => void;
  setInstrumentMappings: (mappings: MappingItem[]) => void;
  setOTCMappings: (mappings: MappingItem[]) => void;
  // Update for any item (instrument or OTC) - looks in both arrays
  updateItemMapping: (id: string, updates: ItemMappingUpdate) => void;
  setIsMoving: (isMoving: boolean) => void;
  setSessionId: (sessionId: string) => void;
  setFileId: (fileId: string) => void;
  setRestoredFiles: (files: UploadedFileInfo[]) => void;
  setSessionName: (name: string) => void;
  setSessionNameValidation: (
    validation: Partial<SessionNameValidation>
  ) => void;
  clearSession: () => void;
  resetWizard: () => void;
  loadSessionData: (sessionData: SessionDataResponse) => void;
  templateId: string | null;
  setTemplateId: (templateId: string | null) => void;
  getIsRestoredSession: () => boolean;
  markStepCompleted: (stepIndex: number) => void;
  clearCompletedSteps: () => void;
  setStepLoading: (loading: boolean) => void;
  setRowCounts: (enabled: number, uploaded: number) => void;
  // Dirty tracking
  pristineState: PristineState;
  setPristineState: (
    step: StepKey,
    data: ConfigureMappingData | AccountCustodianMapping[] | MappingItem[]
  ) => void;
  clearPristineState: (step: StepKey) => void;
  clearAllPristineState: () => void;
}

const dataFeedWizardStore = create<DataFeedWizardState>()(
  devtools(
    persist(
      (set, get) => ({
        activeStep: WizardStep.UPLOAD_FILES,
        activeUploadTab: 'vaults',
        vaultFiles: [],
        excelFiles: [],
        restoredFiles: [],
        returnExcel: false,
        rowsType: RowType.ROW_TYPE_TRANSACTION,
        currentStepButtonState: {
          disabled: true,
          loading: false,
          validationMessage: undefined,
        },
        configureMappingData: {
          accountNumberColumn: null,
          isinColumn: null,
          symbolColumn: null,
          otcColumns: [{ column: '', isIdentifier: false }],
        },
        linkAccountsData: {
          mappings: [],
        },
        custodianOptions: [],
        instrumentMappings: [],
        otcMappings: [],
        isMoving: false,
        stepActions: {},
        sessionId: null,
        fileId: null,
        templateId: null,
        completedSteps: [],
        stepLoading: false,
        pristineState: {},
        sessionName: '',
        sessionNameValidation: {
          isChecking: false,
          isUnique: null,
          error: null,
        },
        enabledRowsCount: 0,
        uploadedRowsCount: 0,

        // Selectors
        getInstrumentItems: () => get().instrumentMappings,
        getOTCItems: () => get().otcMappings,

        setActiveStep: (step: WizardStep) =>
          set({
            activeStep: step,
            currentStepButtonState: {
              disabled: false,
              loading: false,
              validationMessage: undefined,
            },
          }),

        nextStep: () => {
          const currentStep = get().activeStep;
          set({
            activeStep: currentStep + 1,
            currentStepButtonState: {
              disabled: false,
              loading: false,
              validationMessage: undefined,
            },
          });
        },

        prevStep: () => {
          const currentStep = get().activeStep;
          if (currentStep > 0) {
            set({
              activeStep: currentStep - 1,
              currentStepButtonState: {
                disabled: false,
                loading: false,
                validationMessage: undefined,
              },
            });
          }
        },

        setCurrentStepButtonState: (state: ButtonConfig) =>
          set({ currentStepButtonState: state }),

        registerStepActions: (actions: StepActions) =>
          set({ stepActions: actions }),

        clearStepActions: () => set({ stepActions: {} }),

        setActiveUploadTab: (tab: 'vaults' | 'excel') =>
          set({ activeUploadTab: tab }),

        setVaultFiles: (files: DanelFileInfo[]) => set({ vaultFiles: files }),

        setExcelFiles: (files: DanelFileInfo[]) => set({ excelFiles: files }),

        addVaultFiles: (files: DanelFileInfo[]) =>
          set(state => ({ vaultFiles: [...state.vaultFiles, ...files] })),

        addExcelFiles: (files: DanelFileInfo[]) =>
          set(state => ({ excelFiles: [...state.excelFiles, ...files] })),

        removeVaultFile: (index: number) =>
          set(state => ({
            vaultFiles: state.vaultFiles.filter((_, i) => i !== index),
          })),

        removeExcelFile: (index: number) =>
          set(state => ({
            excelFiles: state.excelFiles.filter((_, i) => i !== index),
          })),

        updateVaultFileBank: (index: number, bank: BankType) =>
          set(state => ({
            vaultFiles: state.vaultFiles.map((file, i) =>
              i === index ? { ...file, bank } : file
            ),
          })),

        setReturnExcel: (returnExcel: boolean) => set({ returnExcel }),

        setRowsType: (rowsType: RowType) => set({ rowsType }),

        setConfigureMappingData: (data: Partial<ConfigureMappingData>) =>
          set(state => ({
            configureMappingData: {
              ...state.configureMappingData,
              ...data,
            },
          })),

        addOTCColumn: () =>
          set(state => ({
            configureMappingData: {
              ...state.configureMappingData,
              otcColumns: [
                ...state.configureMappingData.otcColumns,
                { column: '', isIdentifier: false },
              ],
            },
          })),

        removeOTCColumn: (index: number) =>
          set(state => ({
            configureMappingData: {
              ...state.configureMappingData,
              otcColumns: state.configureMappingData.otcColumns.filter(
                (_, i) => i !== index
              ),
            },
          })),

        updateOTCColumn: (index: number, value: string) =>
          set(state => ({
            configureMappingData: {
              ...state.configureMappingData,
              otcColumns: state.configureMappingData.otcColumns.map((col, i) =>
                i === index ? { ...col, column: value } : col
              ),
            },
          })),

        updateOTCColumnIdentifier: (index: number, isIdentifier: boolean) =>
          set(state => ({
            configureMappingData: {
              ...state.configureMappingData,
              otcColumns: state.configureMappingData.otcColumns.map((col, i) =>
                i === index ? { ...col, isIdentifier } : col
              ),
            },
          })),

        setLinkAccountsData: (data: Partial<LinkAccountsData>) =>
          set(state => ({
            linkAccountsData: {
              ...state.linkAccountsData,
              ...data,
            },
          })),

        updateAccountMapping: (
          index: number,
          custodianId: string | null,
          custodianName: string | null,
          portfolioId: string | null,
          clientName: string | null,
          portfolioName: string | null
        ) =>
          set(state => ({
            linkAccountsData: {
              ...state.linkAccountsData,
              mappings: state.linkAccountsData.mappings.map((mapping, i) =>
                i === index
                  ? { ...mapping, custodianId, custodianName, portfolioId, clientName, portfolioName }
                  : mapping
              ),
            },
          })),

        updateAccountUploadEnabled: (index: number, enabled: boolean) =>
          set(state => ({
            linkAccountsData: {
              ...state.linkAccountsData,
              mappings: state.linkAccountsData.mappings.map((mapping, i) =>
                i === index
                  ? { ...mapping, uploadTransactionsEnabled: enabled }
                  : mapping
              ),
            },
          })),

        setCustodianOptions: (options: CustodianOption[]) =>
          set({ custodianOptions: options }),

        setInstrumentMappings: (mappings: MappingItem[]) =>
          set({ instrumentMappings: mappings }),

        setOTCMappings: (mappings: MappingItem[]) =>
          set({ otcMappings: mappings }),

        updateItemMapping: (id: string, updates: ItemMappingUpdate) =>
          set(state => {
            const instrumentIndex = state.instrumentMappings.findIndex(
              item => item.id === id
            );
            if (instrumentIndex > -1) {
              const newInstrumentMappings = [...state.instrumentMappings];
              newInstrumentMappings[instrumentIndex] = {
                ...newInstrumentMappings[instrumentIndex],
                ...updates,
                status:
                  'instrument_source_id' in updates
                    ? updates.instrument_source_id?.source_id
                      ? 'matched'
                      : 'pending'
                    : newInstrumentMappings[instrumentIndex].status,
              };
              return { instrumentMappings: newInstrumentMappings };
            }

            const otcIndex = state.otcMappings.findIndex(
              item => item.id === id
            );
            if (otcIndex > -1) {
              const newOtcMappings = [...state.otcMappings];
              newOtcMappings[otcIndex] = {
                ...newOtcMappings[otcIndex],
                ...updates,
                status:
                  'instrument_source_id' in updates
                    ? updates.instrument_source_id?.source_id
                      ? 'matched'
                      : 'pending'
                    : newOtcMappings[otcIndex].status,
              };
              return { otcMappings: newOtcMappings };
            }

            return state; // Return original state if not found
          }),

        setIsMoving: (isMoving: boolean) => set({ isMoving }),

        setSessionId: (sessionId: string) => set({ sessionId }),

        setFileId: (fileId: string) => set({ fileId }),

        setRestoredFiles: (files: UploadedFileInfo[]) =>
          set({ restoredFiles: files }),

        setSessionName: (name: string) => set({ sessionName: name }),

        setSessionNameValidation: (
          validation: Partial<SessionNameValidation>
        ) =>
          set(state => ({
            sessionNameValidation: {
              ...state.sessionNameValidation,
              ...validation,
            },
          })),

        clearSession: () =>
          set({ sessionId: null, fileId: null, restoredFiles: [] }),

        resetWizard: () =>
          set({
            activeStep: WizardStep.UPLOAD_FILES,
            activeUploadTab: 'vaults',
            vaultFiles: [],
            excelFiles: [],
            restoredFiles: [],
            returnExcel: false,
            rowsType: RowType.ROW_TYPE_TRANSACTION,
            configureMappingData: {
              accountNumberColumn: null,
              isinColumn: null,
              symbolColumn: null,
              otcColumns: [{ column: '', isIdentifier: false }],
            },
            linkAccountsData: {
              mappings: [],
            },
            custodianOptions: [],
            instrumentMappings: [],
            otcMappings: [],
            isMoving: false,
            sessionId: null,
            fileId: null,
            templateId: null,
            completedSteps: [],
            stepLoading: false,
            pristineState: {},
            sessionName: '',
            sessionNameValidation: {
              isChecking: false,
              isUnique: null,
              error: null,
            },
            enabledRowsCount: 0,
            uploadedRowsCount: 0,
          }),

        loadSessionData: (sessionData: SessionDataResponse) => {
          // Extract restored files from session
          const restoredFiles: UploadedFileInfo[] = (
            sessionData.uploaded_files || []
          ).map((f: UploadedFileInfo) => ({
            filename: f.filename || '[MISSING FILENAME]',
            bank_type: f.bank_type,
            upload_tab: f.upload_tab || 'vaults',
          }));

          // Determine active upload tab from restored files
          const hasVaultFiles = restoredFiles.some(
            f => f.upload_tab === 'vaults'
          );
          const hasExcelFiles = restoredFiles.some(
            f => f.upload_tab === 'excel'
          );
          const activeUploadTab =
            hasExcelFiles && !hasVaultFiles ? 'excel' : 'vaults';

          // Reconstruct completed steps - all steps before current_step are completed
          const currentStepIndex =
            sessionData.current_step > 0 ? sessionData.current_step - 1 : 0;
          const completedSteps = Array.from(
            { length: currentStepIndex },
            (_, i) => i
          );

          // Use separate instrument_mappings and otc_mappings from backend
          // Backend now uses snake_case: instrument_mappings, otc_mappings
          const instrumentMappings = sessionData.instrument_mappings;
          const otcMappings = sessionData.otc_mappings;

          set({
            sessionId: sessionData.session_id,
            fileId: sessionData.file_id,
            activeStep: currentStepIndex,
            restoredFiles,
            activeUploadTab,
            completedSteps,
            // Clear any stale validation messages from previous step renders
            currentStepButtonState: {
              disabled: false,
              loading: false,
              validationMessage: undefined,
            },
            configureMappingData: {
              accountNumberColumn: sessionData.account_number_column || null,
              isinColumn: sessionData.isin_column || null,
              symbolColumn: sessionData.symbol_column || null,
              otcColumns: sessionData.otc_columns?.length
                ? sessionData.otc_columns
                : [{ column: '', isIdentifier: false }],
            },
            linkAccountsData: {
              mappings: sessionData.account_mappings || [],
            },
            instrumentMappings,
            otcMappings,
            rowsType: sessionData.rows_type,
            templateId: sessionData.template_id || null,
            // Set session name from restored session
            sessionName:
              (sessionData as SessionDataResponse & { name?: string }).name ||
              '',
            sessionNameValidation: {
              isChecking: false,
              isUnique: true,
              error: null,
            },
            // Set pristine state for all steps from restored session data
            pristineState: {
              step1: {
                accountNumberColumn: sessionData.account_number_column || null,
                isinColumn: sessionData.isin_column || null,
                symbolColumn: sessionData.symbol_column || null,
                otcColumns: sessionData.otc_columns?.length
                  ? sessionData.otc_columns
                  : [{ column: '', isIdentifier: false }],
              },
              step2: sessionData.account_mappings || [],
              step3: instrumentMappings,
              step4: otcMappings,
            },
            // Row counts for upload tracking
            enabledRowsCount: sessionData.enabled_rows_count ?? 0,
            uploadedRowsCount: sessionData.uploaded_rows_count ?? 0,
          });
        },

        setTemplateId: (templateId: string | null) => set({ templateId }),

        getIsRestoredSession: () => {
          const state = get();
          return (
            state.sessionId !== null ||
            (state.restoredFiles.length > 0 &&
              state.vaultFiles.length === 0 &&
              state.excelFiles.length === 0)
          );
        },

        markStepCompleted: (stepIndex: number) =>
          set(state => ({
            completedSteps: state.completedSteps.includes(stepIndex)
              ? state.completedSteps
              : [...state.completedSteps, stepIndex],
          })),

        clearCompletedSteps: () => set({ completedSteps: [] }),

        setStepLoading: (loading: boolean) => set({ stepLoading: loading }),

        setRowCounts: (enabled: number, uploaded: number) =>
          set({ enabledRowsCount: enabled, uploadedRowsCount: uploaded }),

        // Dirty tracking actions
        setPristineState: (
          step: StepKey,
          data: ConfigureMappingData | AccountCustodianMapping[] | MappingItem[]
        ) =>
          set(state => ({
            pristineState: {
              ...state.pristineState,
              [step]: structuredClone(data), // Deep clone to prevent reference sharing
            },
          })),

        clearPristineState: (step: StepKey) =>
          set(state => {
            const newPristine = { ...state.pristineState };
            delete newPristine[step];
            return { pristineState: newPristine };
          }),

        clearAllPristineState: () => set({ pristineState: {} }),
      }),
      {
        name: 'data-feed-wizard-storage',
        partialize: state => ({
          sessionId: state.sessionId,
          fileId: state.fileId,
        }),
      }
    ),
    {
      name: 'data-feed-wizard-store',
    }
  )
);

// Custom hook wrapper
export const useDataFeedWizardStore = () => {
  const store = dataFeedWizardStore();
  return store;
};

// Stable selector hooks - use these instead of getInstrumentItems/getOTCItems
export const useInstrumentItems = () => {
  return dataFeedWizardStore(useShallow(state => state.instrumentMappings));
};

export const useOTCItems = () => {
  return dataFeedWizardStore(useShallow(state => state.otcMappings));
};

export default dataFeedWizardStore;
